// :)
/**
 * Bash hook scripts for Cursor IDE adapter and shared common utilities.
 *
 * CC hooks have been moved to hookScriptsTs.ts (TypeScript + Bun runtime).
 * This file retains the bash common script (sourced by Cursor hooks) and
 * the Cursor-specific adapter scripts.
 */

export const SYNKRO_COMMON_SCRIPT = `#!/bin/bash
# Shared Synkro hook utilities — sourced by all hook scripts.

synkro_log() { echo "[synkro] $1" >&2; }

# Load config
_SYNKRO_CONFIG="$HOME/.synkro/config.env"
if [ -f "$_SYNKRO_CONFIG" ]; then
  set -a; . "$_SYNKRO_CONFIG"; set +a
fi

GATEWAY_URL="\${SYNKRO_GATEWAY_URL:-https://api.synkro.sh}"
CREDS_PATH="\${SYNKRO_CREDENTIALS_PATH:-$HOME/.synkro/credentials.json}"

synkro_load_jwt() {
  if [ ! -f "$CREDS_PATH" ]; then echo ""; return 1; fi
  jq -r '.access_token // empty' "$CREDS_PATH" 2>/dev/null
}

synkro_refresh_jwt() {
  # Lock via mkdir (atomic on all Unix including macOS — no flock needed)
  local lockdir="\${CREDS_PATH}.lockdir"
  if ! mkdir "$lockdir" 2>/dev/null; then
    # Another hook is refreshing — wait and re-read
    local _w=0
    while [ -d "$lockdir" ] && [ $_w -lt 5 ]; do sleep 0.5; _w=$((_w+1)); done
    JWT=$(jq -r '.access_token // empty' "$CREDS_PATH" 2>/dev/null)
    return 0
  fi
  trap "rmdir \\"$lockdir\\" 2>/dev/null" RETURN

  # Re-check expiry — another hook may have just refreshed
  local p2 exp2 now2
  p2=$(printf '%s' "$JWT" | cut -d. -f2)
  case $((\${#p2} % 4)) in 2) p2="\${p2}==";; 3) p2="\${p2}=";; esac
  exp2=$(printf '%s' "$p2" | tr '_-' '/+' | base64 -D 2>/dev/null | jq -r '.exp // 0' 2>/dev/null)
  now2=$(date -u +%s)
  if [ $((exp2 - now2)) -ge 60 ]; then return 0; fi

  local rt
  rt=$(jq -r '.refresh_token // empty' "$CREDS_PATH" 2>/dev/null)
  if [ -z "$rt" ]; then return 1; fi
  local resp
  resp=$(curl -sS -X POST "\${GATEWAY_URL}/api/auth/refresh" \\
    -H "Content-Type: application/json" \\
    -d "$(jq -n --arg rt "$rt" '{refresh_token:$rt}')" \\
    --max-time 4 2>/dev/null)
  local new_at
  new_at=$(echo "$resp" | jq -r '.access_token // empty' 2>/dev/null)
  if [ -z "$new_at" ]; then return 1; fi
  local new_rt
  new_rt=$(echo "$resp" | jq -r '.refresh_token // empty' 2>/dev/null)
  [ -z "$new_rt" ] && new_rt="$rt"
  local tmp="\${CREDS_PATH}.synkro.tmp"
  local existing
  existing=$(cat "$CREDS_PATH" 2>/dev/null)
  if [ -z "$existing" ] || ! echo "$existing" | jq -e '.' >/dev/null 2>&1; then
    existing='{}'
  fi
  echo "$existing" | jq --arg at "$new_at" --arg rt "$new_rt" '. + {access_token:$at,refresh_token:$rt}' > "$tmp" 2>/dev/null && mv "$tmp" "$CREDS_PATH"
  JWT="$new_at"
}

synkro_ensure_fresh_jwt() {
  [ -z "$JWT" ] && return 1
  local p exp now
  p=$(printf '%s' "$JWT" | cut -d. -f2)
  case $((\${#p} % 4)) in 2) p="\${p}==";; 3) p="\${p}=";; esac
  exp=$(printf '%s' "$p" | tr '_-' '/+' | base64 -D 2>/dev/null | jq -r '.exp // 0' 2>/dev/null)
  now=$(date -u +%s)
  [ $((exp - now)) -lt 60 ] && synkro_refresh_jwt
}

synkro_detect_repo() {
  local cwd="\${1:-.}"
  if command -v git >/dev/null 2>&1; then
    local r
    r=$(git -C "$cwd" remote get-url origin 2>/dev/null || true)
    [ -n "$r" ] && echo "$r" | sed -E 's|^git@[^:]+:||; s|^https?://[^/]+/||; s|\\.git$||' && return
  fi
  echo ""
}

synkro_channel_up() {
  (exec 3<>/dev/tcp/127.0.0.1/\${SYNKRO_CHANNEL_PORT:-8929}) 2>/dev/null && exec 3<&- 3>&-
}

# Fetch hook config. Sets SYNKRO_CAPTURE_DEPTH, SYNKRO_TIER, SYNKRO_RULES, SYNKRO_SILENT, SYNKRO_POLICY_NAME.
_SYNKRO_MCP_JWT_FILE="$HOME/.synkro/.mcp-jwt"

synkro_load_config() {
  local resp
  resp=$(curl -sS "\${GATEWAY_URL}/api/v1/hook/config\${1:+?$1}" -H "Authorization: Bearer $JWT" --max-time 4 2>/dev/null || echo "")
  if [ -z "$resp" ]; then return; fi
  SYNKRO_CAPTURE_DEPTH=$(echo "$resp" | jq -r '.capture_depth // "local_only"' 2>/dev/null)
  SYNKRO_TIER=$(echo "$resp" | jq -r '.tier // "standard"' 2>/dev/null)
  SYNKRO_SILENT=$(echo "$resp" | jq -r '.silent_mode // false' 2>/dev/null)
  SYNKRO_POLICY_NAME=$(echo "$resp" | jq -r '.active_policy_name // empty' 2>/dev/null)
  SYNKRO_RULES=$(echo "$resp" | jq -c '[.rules[]? | {rule_id,text,severity,category,mode}]' 2>/dev/null || echo "[]")
}

synkro_local_capture() {
  local event_json="$1"
  local ts mcp_token line
  ts=$(date -u +"%Y-%m-%dT%H:%M:%S.000Z")
  line=$(echo "$event_json" | jq -c --arg ts "$ts" '. + {_ts: $ts}' 2>/dev/null)
  [ -z "$line" ] && return
  [ -f "$_SYNKRO_MCP_JWT_FILE" ] || return
  mcp_token=$(cat "$_SYNKRO_MCP_JWT_FILE" 2>/dev/null)
  [ -z "$mcp_token" ] && return
  curl -fsS -m 2 -X POST "http://127.0.0.1:\${SYNKRO_MCP_PORT:-8931}/api/ingest" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer \${mcp_token}" \\
    -d "$(jq -nc --argjson e "$line" '{data: $e}')" >/dev/null 2>&1 &
}

synkro_tag() {
  if [ "$SYNKRO_SILENT" = "true" ]; then echo "[synkro:silent]"; return; fi
  local route="\${1:-\$(synkro_route)}"
  local rs="\${SYNKRO_POLICY_NAME:-all}"
  echo "[synkro:\${route}:\${rs}]"
}

synkro_route() {
  [ "$SYNKRO_CAPTURE_DEPTH" = "local_only" ] && echo "local" && return
  synkro_channel_up && echo "local" && return
  echo "cloud"
}

SYNKRO_CONSENT_FILE="$HOME/.synkro/.local-consent"

_TAB=\$(printf '\\t')

synkro_consent_grant() {
  local sid="\$1" hash="\$2"
  printf '%s\\t%s\\tactive\\n' "$sid" "$hash" >> "$SYNKRO_CONSENT_FILE" 2>/dev/null || true
}

synkro_consent_has_active() {
  local sid="\$1" hash="\$2"
  grep -q "^\${sid}\${_TAB}\${hash}\${_TAB}active\$" "$SYNKRO_CONSENT_FILE" 2>/dev/null
}

synkro_consent_consume() {
  local sid="\$1" hash="\$2"
  [ ! -f "$SYNKRO_CONSENT_FILE" ] && return
  local tmp="\${SYNKRO_CONSENT_FILE}.tmp"
  local pat="\${sid}\${_TAB}\${hash}\${_TAB}active"
  local rep="\${sid}\${_TAB}\${hash}\${_TAB}consumed"
  awk -v p="$pat" -v r="$rep" '{if(\$0==p)print r;else print}' "$SYNKRO_CONSENT_FILE" > "$tmp" 2>/dev/null && mv "$tmp" "$SYNKRO_CONSENT_FILE" 2>/dev/null || true
}

synkro_post_with_retry() {
  local url="$1" body="$2" timeout="\${3:-8}"
  local resp
  resp=$(curl -sS -X POST "$url" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer $JWT" \\
    -d "$body" --max-time "$timeout" 2>/dev/null || echo "")
  if echo "$resp" | grep -qE '"detail":"Token has expired|"detail":"Invalid or expired token'; then
    if synkro_refresh_jwt; then
      resp=$(curl -sS -X POST "$url" \\
        -H "Content-Type: application/json" \\
        -H "Authorization: Bearer $JWT" \\
        -d "$body" --max-time "$timeout" 2>/dev/null || echo "")
    fi
  fi
  echo "$resp"
}
`;


// ─── Cursor IDE adapter scripts (legacy bash — install writes TypeScript from hookScriptsTs.ts) ───

export const CURSOR_BASH_JUDGE_SCRIPT = `#!/bin/bash
SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
. "$SCRIPT_DIR/_synkro-common.sh"

JWT=$(synkro_load_jwt)
if [ -z "$JWT" ]; then echo '{}'; exit 0; fi
synkro_ensure_fresh_jwt

PAYLOAD=$(cat)
if [ -z "$PAYLOAD" ]; then echo '{}'; exit 0; fi

COMMAND=$(echo "$PAYLOAD" | jq -r '.command // empty' 2>/dev/null)
if [ -z "$COMMAND" ]; then echo '{}'; exit 0; fi

CWD=$(echo "$PAYLOAD" | jq -r '.cwd // empty' 2>/dev/null)
SESSION_ID=$(echo "$PAYLOAD" | jq -r '.conversation_id // empty' 2>/dev/null)
GIT_REPO=$(synkro_detect_repo "\${CWD:-.}")

CMD_SHORT=$(printf '%s' "$COMMAND" | head -c 80)
synkro_log "bashGuard checking: $CMD_SHORT"

synkro_load_config
if [ "$SYNKRO_SILENT" = "true" ]; then
  echo '{}'; exit 0
fi

BODY=$(jq -n \\
  --arg cmd "$COMMAND" \\
  --arg session_id "$SESSION_ID" \\
  --arg cwd "$CWD" \\
  --arg repo "$GIT_REPO" \\
  '{
    hook_event: "PreToolUse",
    tool_name: "Bash",
    tool_input: {command: $cmd},
    response_format: "cursor",
    session_id: (if ($session_id | length) > 0 then $session_id else null end),
    cwd: (if ($cwd | length) > 0 then $cwd else null end),
    repo: (if ($repo | length) > 0 then $repo else null end)
  }')

RESP=$(synkro_post_with_retry "\${GATEWAY_URL}/api/v1/hook/judge" "$BODY" 6)

if [ -z "$RESP" ]; then
  synkro_log "bashGuard $CMD_SHORT → error (timeout)"
  echo '{}'; exit 0
fi

# Server returns cursor-format directly in hook_response
if echo "$RESP" | jq -e '.hook_response' >/dev/null 2>&1; then
  echo "$RESP" | jq -c '.hook_response'
else
  echo '{}'
fi
exit 0
`;

export const CURSOR_EDIT_PRECHECK_SCRIPT = `#!/bin/bash
SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
. "$SCRIPT_DIR/_synkro-common.sh"

JWT=$(synkro_load_jwt)
if [ -z "$JWT" ]; then echo '{}'; exit 0; fi
synkro_ensure_fresh_jwt

PAYLOAD=$(cat)
if [ -z "$PAYLOAD" ]; then echo '{}'; exit 0; fi

TOOL_NAME=$(echo "$PAYLOAD" | jq -r '.tool_name // empty' 2>/dev/null)
CWD=$(echo "$PAYLOAD" | jq -r '.cwd // empty' 2>/dev/null)
SESSION_ID=$(echo "$PAYLOAD" | jq -r '.conversation_id // empty' 2>/dev/null)
GIT_REPO=$(synkro_detect_repo "\${CWD:-.}")

FILE_PATH=$(echo "$PAYLOAD" | jq -r '.tool_input.file_path // .tool_input.path // .tool_input.target_file // empty' 2>/dev/null)
CONTENT=$(echo "$PAYLOAD" | jq -r '.tool_input.content // .tool_input.new_string // .tool_input.code_edit // empty' 2>/dev/null)
if [ -z "$FILE_PATH" ]; then echo '{}'; exit 0; fi

BASENAME=$(basename "$FILE_PATH" 2>/dev/null || echo "$FILE_PATH")
synkro_log "editGuard checking: $BASENAME"

synkro_load_config
if [ "$SYNKRO_SILENT" = "true" ]; then
  echo '{}'; exit 0
fi

BODY=$(jq -n \\
  --arg file_path "$FILE_PATH" \\
  --arg content "$CONTENT" \\
  --arg session_id "$SESSION_ID" \\
  --arg cwd "$CWD" \\
  --arg repo "$GIT_REPO" \\
  '{
    hook_event: "PreToolUse",
    tool_name: "Edit",
    tool_input: {file_path: $file_path, content: $content},
    file_path: $file_path,
    content: $content,
    response_format: "cursor",
    session_id: (if ($session_id | length) > 0 then $session_id else null end),
    cwd: (if ($cwd | length) > 0 then $cwd else null end),
    repo: (if ($repo | length) > 0 then $repo else null end)
  }')

RESP=$(synkro_post_with_retry "\${GATEWAY_URL}/api/v1/hook/judge" "$BODY" 8)

if [ -z "$RESP" ]; then
  synkro_log "editGuard $BASENAME → error (timeout)"
  echo '{}'; exit 0
fi

if echo "$RESP" | jq -e '.hook_response' >/dev/null 2>&1; then
  echo "$RESP" | jq -c '.hook_response'
else
  echo '{}'
fi
exit 0
`;

export const CURSOR_EDIT_CAPTURE_SCRIPT = `#!/bin/bash
SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
. "$SCRIPT_DIR/_synkro-common.sh"

JWT=$(synkro_load_jwt)
if [ -z "$JWT" ]; then echo '{}'; exit 0; fi

PAYLOAD=$(cat)
if [ -z "$PAYLOAD" ]; then echo '{}'; exit 0; fi

FILE_PATH=$(echo "$PAYLOAD" | jq -r '.file_path // empty' 2>/dev/null)
if [ -z "$FILE_PATH" ]; then echo '{}'; exit 0; fi

CWD=$(echo "$PAYLOAD" | jq -r '.cwd // .workspace_roots[0] // empty' 2>/dev/null)
SESSION_ID=$(echo "$PAYLOAD" | jq -r '.conversation_id // empty' 2>/dev/null)
GIT_REPO=$(synkro_detect_repo "\${CWD:-.}")
BASENAME=$(basename "$FILE_PATH" 2>/dev/null || echo "$FILE_PATH")

FULL_PATH="$FILE_PATH"
[ -n "$CWD" ] && FULL_PATH="$CWD/$FILE_PATH"
FULL_CONTENT=""
[ -f "$FULL_PATH" ] && FULL_CONTENT=$(head -c 50000 "$FULL_PATH" 2>/dev/null || true)

DEPS_JSON="{}"
_PKG_DIR="\${CWD:-.}"
while [ "$_PKG_DIR" != "/" ]; do
  if [ -f "$_PKG_DIR/package.json" ]; then
    DEPS_JSON=$(jq -c '(.dependencies // {}) + (.devDependencies // {})' "$_PKG_DIR/package.json" 2>/dev/null || echo "{}")
    break
  fi
  _PKG_DIR=$(dirname "$_PKG_DIR")
done

synkro_log "editScan $BASENAME"

(
  BODY=$(jq -n \\
    --arg file_path "$FILE_PATH" --arg content "$FULL_CONTENT" \\
    --arg session_id "$SESSION_ID" --arg cwd "$CWD" --arg repo "$GIT_REPO" \\
    --argjson deps "$DEPS_JSON" \\
    '{capture_type:"edit_scan",tool_input:{file_path:$file_path,content:$content},edit_verdict:{ok:true},dependencies:$deps}
      + (if ($session_id | length) > 0 then {session_id:$session_id} else {} end)
      + (if ($cwd | length) > 0 then {cwd:$cwd} else {} end)
      + (if ($repo | length) > 0 then {repo:$repo} else {} end)')
  curl -sS -X POST "\${GATEWAY_URL}/api/v1/hook/capture" \\
    -H "Content-Type: application/json" -H "Authorization: Bearer $JWT" \\
    -d "$BODY" --max-time 10 >/dev/null 2>&1 || true
) &
disown 2>/dev/null || true

echo '{}'
exit 0
`;

export const CURSOR_BASH_FOLLOWUP_SCRIPT = `#!/bin/bash
SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
. "$SCRIPT_DIR/_synkro-common.sh"

JWT=$(synkro_load_jwt)
if [ -z "$JWT" ]; then echo '{}'; exit 0; fi

PAYLOAD=$(cat)
TOOL_NAME=$(echo "$PAYLOAD" | jq -r '.tool_name // empty' 2>/dev/null)
case "$TOOL_NAME" in Shell|Bash|terminal|run_terminal_cmd|execute_command) ;; *) echo '{}'; exit 0 ;; esac

SESSION_ID=$(echo "$PAYLOAD" | jq -r '.conversation_id // empty' 2>/dev/null)
TOOL_USE_ID=$(echo "$PAYLOAD" | jq -r '.tool_use_id // empty' 2>/dev/null)

IS_ERROR=$(echo "$PAYLOAD" | jq -r '.tool_result.is_error // false' 2>/dev/null)
CMD=$(echo "$PAYLOAD" | jq -r '.tool_input.command // empty' 2>/dev/null)
CMD_HASH=""
if [ -n "$CMD" ]; then
  CMD_HASH=$(printf '%s' "$CMD" | shasum -a 256 | cut -c1-16)
fi

if [ -n "$CMD_HASH" ] && [ -n "$SESSION_ID" ]; then
  if [ "$IS_ERROR" = "false" ]; then
    synkro_consent_consume "$SESSION_ID" "$CMD_HASH"
  else
    if ! synkro_consent_has_active "$SESSION_ID" "$CMD_HASH"; then
      synkro_consent_grant "$SESSION_ID" "$CMD_HASH"
    fi
  fi
fi

if [ -n "$SESSION_ID" ] && [ -n "$TOOL_USE_ID" ]; then
  (
    BODY=$(jq -n --arg sid "$SESSION_ID" --arg tid "$TOOL_USE_ID" \\
      --argjson err "$IS_ERROR" --arg ch "$CMD_HASH" \\
      '{capture_type:"bash_followup",session_id:$sid,tool_use_id:$tid,is_error:$err,command_hash:$ch}')
    curl -sS -X POST "\${GATEWAY_URL}/api/v1/hook/capture" \\
      -H "Content-Type: application/json" -H "Authorization: Bearer $JWT" \\
      -d "$BODY" --max-time 3 >/dev/null 2>&1 || true
  ) &
  disown 2>/dev/null || true
fi

echo '{}'
exit 0
`;
